import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import type { CopilotAnswer } from '@/services/copilot';

/** Render único de respuestas del copiloto — lo usan el panel flotante y la
 *  página /assistant: misma inteligencia, mismas fuentes citadas. */
export function AnswerView({ answer, onOpenProfile }: {
  answer: CopilotAnswer;
  onOpenProfile?: (clientId: string) => void;
}) {
  const { t } = useTranslation();
  switch (answer.kind) {
    case 'missing':
      return (
        <div>
          <p className="text-sm leading-relaxed">
            {answer.items.length === 0
              ? t('copilot.v2.nothingMissing', { name: answer.clientName })
              : t('copilot.v2.missingIntro', { name: answer.clientName, count: answer.items.length })}
          </p>
          {answer.items.length > 0 && (
            <ul className="mt-2 space-y-1">
              {answer.items.map((it, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-warning" />
                  <span>
                    {it.label}
                    <span className="block text-[11px] text-muted-foreground">{t('copilot.v2.source', { title: it.requestTitle })}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {onOpenProfile && (
            <button type="button" onClick={() => onOpenProfile(answer.clientId)}
              className="focus-ring mt-2 rounded text-xs font-semibold text-primary underline-offset-2 hover:underline">
              {t('copilot.v2.openProfile', { name: answer.clientName })}
            </button>
          )}
        </div>
      );
    case 'noConsent':
      return <p className="text-sm leading-relaxed">{t('copilot.v2.noConsent', { name: answer.clientName })}</p>;
    case 'clientNotFound':
      return <p className="text-sm leading-relaxed">{t('copilot.v2.notFound', { query: answer.query })}</p>;
    case 'summary': {
      const order = ['pending', 'received', 'in_review', 'completed', 'signed', 'delivered'] as const;
      return (
        <div>
          <p className="mb-2 text-sm">{t('copilot.v2.summaryIntro')}</p>
          <ul className="grid grid-cols-2 gap-1.5">
            {order.map((k) => (
              <li key={k} className="flex items-center justify-between rounded-lg bg-muted/30 px-2.5 py-1.5 text-xs">
                <span className="text-muted-foreground">{t(`status.${k}`)}</span>
                <span className="font-semibold tabular-nums">{answer.counts[k] ?? 0}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-muted-foreground">{t('copilot.v2.summarySource')}</p>
        </div>
      );
    }
    case 'llm':
      // VLT-382: respuesta de Claude — con la nota de gobernanza SIEMPRE
      // visible (la IA no da conclusiones fiscales/legales; decide el humano).
      // VLT-399 (spec §17): automatización EXPLICABLE — si Loruel usó una
      // herramienta real, el usuario lo ve dicho con palabras llanas.
      return (
        <div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{answer.text}</p>
          {(answer.toolsUsed ?? []).length > 0 && (
            <p className="mt-1.5 text-[10.5px] text-muted-foreground">
              {[...new Set((answer.toolsUsed ?? []).map((u) => t(`copilot.v2.tools.${u.name}`, u.name)))].join(' · ')}
            </p>
          )}
          {(answer.masked ?? 0) > 0 && (
            // VLT-154: se ocultaron datos sensibles — se dice, sin mostrarlos.
            <p className="mt-1.5 flex items-center gap-1 text-[10.5px] text-success" data-testid="ai-masked-note">
              <ShieldCheck className="h-3 w-3" />{t('copilot.v2.masked', { count: answer.masked })}
            </p>
          )}
          <p className="mt-2 border-t border-border/50 pt-1.5 text-[10.5px] text-muted-foreground">{t('copilot.v2.llmNote')}</p>
        </div>
      );
    default:
      return <p className="text-sm leading-relaxed">{t('copilot.v2.help')}</p>;
  }
}
